import { defineStore } from 'pinia'
import {
  EnumerationsAAPReportCategory,
  EnumerationsAAPReportClassification,
} from 'client'
import { AxiosError } from 'axios'
import { useAAPSubPlansStore } from './AAPSubPlans'
import { usePlansStore } from './plans'
import { useAAPExportReportsStore } from './AAPExportReports'
import { useNavigationStore } from './navigation'
import type { Router } from 'vue-router'
import type {
  QueriesGetPlansResponse,
  TransferObjectsGetReportsResponseDTO,
  TransferObjectsPlanInfo,
} from 'client'
import type {
  ICheckableReportGroups,
  IReportsWithCheckableReportGroups,
  TReportTableCheckTriggerLabel,
} from '~/constants/types'
import { LABEL, NAME, STORE } from '~/constants/strings'
import { OReportsTableCheckLabels } from '~/constants/types'
import { HNRV2, handleError } from '~/helpers'
import { api } from '~/services'
import { BASE_GUID } from '~/constants'

interface RootState {
  loading: boolean
  selectedPlan: TransferObjectsPlanInfo | null
  reportsSectionPlans: QueriesGetPlansResponse
  reportsSearchTerm: string
  reportGroups: IReportsWithCheckableReportGroups[]
  checkTriggers: {
    label: TReportTableCheckTriggerLabel
    value: any
  }[]
  error: boolean
}

export const useReportStore = defineStore(STORE.REPORT, {
  state: (): RootState => ({
    loading: false,
    selectedPlan: null,
    reportsSearchTerm: '',
    reportGroups: [],
    reportsSectionPlans: {
      allPlans: [],
      mostRecentPlans: [],
      mostRecentPlan: null as any,
      message: '',
    },
    checkTriggers: [
      {
        label: OReportsTableCheckLabels.ALL,
        value: 'all',
      },
      {
        label: OReportsTableCheckLabels.NONE,
        value: 'none',
      },
      {
        label: OReportsTableCheckLabels.PRIMARY,
        value: EnumerationsAAPReportClassification.Primary,
      },
      {
        label: OReportsTableCheckLabels.SUPPORT,
        value: EnumerationsAAPReportClassification.Support,
      },
      {
        label: OReportsTableCheckLabels.PRIMARYANDSUPPORT,
        value: 'both',
      },
    ],
    error: false,
  }),
  getters: {
    isAAPSummaries(): boolean {
      return (
        (this.$router as Router).currentRoute.value.name
          ?.toString()
          .includes(NAME.SUMMARIES) ?? false
      )
    },
    filteredReportGroups(): IReportsWithCheckableReportGroups[] {
      const searchTerm = this.reportsSearchTerm.toString().toLowerCase()
      if (!searchTerm || this.isAAPSummaries) return this.reportGroups

      const filteredReportGroups: IReportsWithCheckableReportGroups[] =
        this.reportGroups.reduce<IReportsWithCheckableReportGroups[]>(
          (acc, reportGroup) => {
            const matchingReportsInGroup = reportGroup.reports.filter((d) =>
              d.displayName.toString().toLowerCase().includes(searchTerm)
            )
            if (matchingReportsInGroup.length > 0) {
              acc.push({
                ...reportGroup,
                reports: matchingReportsInGroup,
              })
            }
            return acc
          },
          []
        )
      return filteredReportGroups
    },
    currentlyCheckedReports(): ICheckableReportGroups[] {
      let checkedReportsArr: any[] = []
      this.reportGroups.forEach(
        (reportGroup: IReportsWithCheckableReportGroups) => {
          const checkedReports = reportGroup.reports.filter(
            (report) => report.checked
          )
          checkedReportsArr = [...checkedReportsArr, ...checkedReports]
        }
      )
      return checkedReportsArr
    },
    isNarrativeReportSelected(): boolean {
      return this.currentlyCheckedReports.some((checkedReport) => {
        return (
          checkedReport.aapReportCategoryID ===
          EnumerationsAAPReportCategory.Narratives
        )
      })
    },
    isUpdatePlanSelected(): boolean {
      return this.selectedPlan?.updatePlan ?? false
    },
    isSelectedPlanIDBaseGuid(): boolean {
      return this.selectedPlan?.planID === BASE_GUID
    },
  },
  actions: {
    async setPlans() {
      const PlansStore = usePlansStore()
      const aapSubPlansStore = useAAPSubPlansStore()

      this.reportsSectionPlans = PlansStore.plans
      this.selectedPlan = PlansStore.plans.mostRecentPlan

      if (!this.isSelectedPlanIDBaseGuid) {
        await aapSubPlansStore.extractSubPlans()
        await this.getReportGroupsFromPlan(this.selectedPlan?.planID)
      }
    },
    async getReportGroupsFromPlan(planID: string) {
      if (this.isAAPSummaries) {
        const selectedUpdatePlan = this.reportsSectionPlans.allPlans.find(
          (plan) => plan.planID === planID
        )

        this.reportGroups = [
          {
            groupName: 'Summaries',
            reportGroupID: '',
            reports: [
              {
                aapReport2ClientID: 'overall',
                displayName: 'Overall Summaries',
                reportCode: '1',
                aapReportCategoryID: null,
                aapReportClassificationID: null,
                aapReportSettingsCategoryID: null,
                checked: false,
              },
              {
                aapReport2ClientID: 'plan-summary',
                displayName: selectedUpdatePlan?.updatePlan
                  ? 'Progress Summaries'
                  : 'Plan Summaries',
                reportCode: '2',
                aapReportCategoryID: null,
                aapReportClassificationID: null,
                aapReportSettingsCategoryID: null,
                checked: false,
              },
            ],
          },
        ]
        return
      }

      const reportsFromSelectedPlan = await HNRV2({
        fn: () => api.aapReports.apiAAPReportsPlanPlanIDReportGroupsGet(planID),
      })

      if (reportsFromSelectedPlan instanceof AxiosError) {
        return
      }

      this.reportGroups = reportsFromSelectedPlan.map((reportGroup) => ({
        ...reportGroup,
        reports: reportGroup.reports.map((report) => ({
          ...report,
          checked: false,
        })),
      }))
    },
    async updatePlanLastAccessedDate(planID: string): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.plans.apiPlansReportLastAccessedDatePut({
            planID,
          }),
      })

      if (response instanceof AxiosError) {
        return false
      }
      return true
    },
    async onSelectPlan(clickedPlan: TransferObjectsPlanInfo) {
      try {
        this.loading = true

        const PlansStore = usePlansStore()
        const AAPExportReportsStore = useAAPExportReportsStore()

        AAPExportReportsStore.runReportsOn = LABEL.THIS_PLAN

        const response = await this.updatePlanLastAccessedDate(
          clickedPlan.planID
        )

        if (!response) {
          this.error = true
          return
        }

        await PlansStore.getPlans()
        await this.setPlans()

        const NavigationStore = useNavigationStore()

        await NavigationStore.setNavigationItems({
          serviceID: NavigationStore.selectedTopNavigationServiceId,
          forceUpdate: true,
        })
      } finally {
        this.loading = false
      }
    },
    checkReportsIf(
      updateText: string,
      condition:
        | boolean
        | ((report: TransferObjectsGetReportsResponseDTO) => boolean)
    ) {
      this.reportGroups = this.reportGroups.map((reportGroup) => ({
        ...reportGroup,
        reports: reportGroup.reports.map((report) => {
          const newChecked =
            typeof condition === 'function' ? condition(report) : condition

          return {
            ...report,
            checked: newChecked,
          }
        }),
      }))
    },
    onCheckTriggerClick(trigger: TReportTableCheckTriggerLabel) {
      const checkTrigger = this.checkTriggers.find(
        ({ label }) => trigger === label
      )
      if (!checkTrigger) {
        return
      }

      const noneEnums: TReportTableCheckTriggerLabel[] = [
        OReportsTableCheckLabels.ALL,
        OReportsTableCheckLabels.NONE,
        OReportsTableCheckLabels.PRIMARYANDSUPPORT,
      ]
      if (noneEnums.includes(trigger)) {
        switch (trigger) {
          case OReportsTableCheckLabels.ALL:
            this.checkReportsIf(checkTrigger.updateText, true)
            break
          case OReportsTableCheckLabels.NONE:
            this.checkReportsIf(checkTrigger.updateText, false)
            break
          case OReportsTableCheckLabels.PRIMARYANDSUPPORT:
            this.checkReportsIf(checkTrigger.updateText, (report) =>
              [
                EnumerationsAAPReportClassification.Primary,
                EnumerationsAAPReportClassification.Support,
              ]
                .map((v) => v.toString())
                .includes(report.aapReportClassificationID?.toString() || '')
            )
            break
          default:
            break
        }
      } else {
        this.checkReportsIf(
          checkTrigger.updateText,
          (report) =>
            report.aapReportClassificationID?.toString() ===
            checkTrigger?.value.toString()
        )
      }
    },
    async initializeReportSection() {
      try {
        const NavigationStore = useNavigationStore()
        const AAPExportReportsStore = useAAPExportReportsStore()

        // to avoid "AAP Reports" showing on "AAP Summaries" for a brief amount of time
        this.reportGroups = []
        this.loading = true

        await AAPExportReportsStore.getExportSettings()
        await this.setPlans()
        NavigationStore.addReportLinksAndRoutes()
      } catch (error) {
        handleError(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    async onRetryClick() {
      this.error = false
      if (this.selectedPlan) {
        await this.onSelectPlan(this.selectedPlan)
      } else {
        await this.initializeReportSection()
      }
    },
  },
})
